// src/component/premium/PricingPlan.jsx

import React, { useState, useEffect } from "react";
import { Container, Col, Row } from "react-bootstrap";
import { Typography, Card, Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "../Api/instance";
import { PageHeader } from "../ui/PageHeaders";
import { PrimaryButton } from "../ui/Buttons";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { styled } from "@mui/material/styles";

const PricingPageContainer = styled(Box)(({ theme }) => ({
  minHeight: "85vh",
  padding: "32px 0",
  backgroundColor: theme.palette.background.default,
}));

const PriceBoxCard = styled(Card)(({ theme }) => ({
  borderRadius: "24px",
  padding: "32px",
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: "none",
  textAlign: "center",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  transition: "transform 0.2s ease, box-shadow 0.2s ease",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "var(--color-shadow-hover)",
  },
}));

export default function PricingPlan() {
  const [data, setData] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await axiosInstance.get("get/U_ViewPlans.php");
      if (Array.isArray(response.data)) {
        const obj = response.data.map((item) => ({
          title: item.title,
          amount: item.amount,
          duration: item.duration,
          description: item.description,
        }));
        setData(obj);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  return (
    <PricingPageContainer>
      <Container>
        {/* Modernized Page Header */}
        <PageHeader 
          title="Our Pricing Plans" 
          subtitle="Simple, transparent plans designed to fit your licensure preparation schedules."
          onBack={() => navigate("/plan")}
        />

        <Row className="g-4 justify-content-center">
          {data.map((plan, index) => (
            <Col xs={12} sm={6} md={4} lg={3} key={index}>
              <PriceBoxCard>
                <Box>
                  <Typography 
                    variant="h3" 
                    style={{ 
                      fontWeight: 800, 
                      color: "var(--color-primary)", 
                      textTransform: "uppercase", 
                      fontSize: "1rem", 
                      letterSpacing: "0.02em",
                      marginBottom: "16px" 
                    }}
                  >
                    {plan.title || "Premium Option"}
                  </Typography>

                  <Typography variant="h2" style={{ fontWeight: 850, marginBottom: "8px" }}>
                    ${plan.amount}
                  </Typography>
                  
                  <Typography variant="body2" color="textSecondary" style={{ fontWeight: 600, marginBottom: "20px" }}>
                    Active for {plan.duration} Month{parseInt(plan.duration) > 1 ? 's' : ''}
                  </Typography>

                  {plan.description && (
                    <Box style={{ textAlign: "left", borderTop: "1px solid var(--color-border)", paddingTop: "16px", marginBottom: "24px" }}>
                      <Box style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
                        <CheckCircleOutlineIcon fontSize="small" style={{ color: "var(--color-success)", marginTop: "2px" }} />
                        <Typography variant="body2" color="textSecondary" style={{ lineHeight: 1.5, fontWeight: 550 }}>
                          {plan.description}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>

                <PrimaryButton
                  onClick={() => navigate("/plan")}
                  style={{ width: "100%", height: "42px", borderRadius: "12px" }}
                >
                  Buy Now
                </PrimaryButton>
              </PriceBoxCard>
            </Col>
          ))}
        </Row>
      </Container>
    </PricingPageContainer>
  );
}
